import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'

export interface KnowledgeDocumentVersionSummary {
  id: string
  versionNumber: number
  changeNote: string
  createdByEmail: string | null
  dateCreated: string
  isCurrent: boolean
}

export interface KnowledgeDocumentVersion extends KnowledgeDocumentVersionSummary {
  content: string
}

export interface KnowledgeDocumentListItem {
  id: string
  slug: string
  title: string
  folder: string | null
  folderPath: string
  isEnabled: boolean
  isPrivate: boolean
  currentVersionNumber: number | null
  currentVersionUpdatedAt: string | null
  currentVersionUpdatedByEmail: string | null
  hasDraft: boolean
  draftUpdatedAt: string | null
}

export interface KnowledgeDocumentDetail {
  id: string
  slug: string
  title: string
  folder: string | null
  folderPath: string
  isEnabled: boolean
  isPrivate: boolean
  currentVersion: KnowledgeDocumentVersion | null
  draftContent: string
  draftUpdatedAt: string | null
  draftUpdatedByEmail: string | null
}

const BASE = '/api/v1/insure/internal/knowledge-documents'

function listDocuments(): Promise<KnowledgeDocumentListItem[]> {
  return axios.get(`${BASE}/`).then((r) => {
    const data = toCamelCase(r.data)
    const results = (data as { results?: KnowledgeDocumentListItem[] }).results
    return (results ?? (data as KnowledgeDocumentListItem[])) as KnowledgeDocumentListItem[]
  })
}

function getDocument(slug: string): Promise<KnowledgeDocumentDetail> {
  return axios.get(`${BASE}/${slug}/`).then((r) => toCamelCase(r.data) as KnowledgeDocumentDetail)
}

function listVersions(slug: string): Promise<KnowledgeDocumentVersionSummary[]> {
  return axios.get(`${BASE}/${slug}/versions/`).then((r) => {
    const data = toCamelCase(r.data)
    return data as KnowledgeDocumentVersionSummary[]
  })
}

function getVersion(slug: string, versionId: string): Promise<KnowledgeDocumentVersion> {
  return axios
    .get(`${BASE}/${slug}/versions/${versionId}/`)
    .then((r) => toCamelCase(r.data) as KnowledgeDocumentVersion)
}

function createDocument(data: {
  title: string
  content?: string
  folder?: string | null
}): Promise<KnowledgeDocumentDetail> {
  return axios.post(`${BASE}/`, toSnakeCase(data)).then((r) => toCamelCase(r.data) as KnowledgeDocumentDetail)
}

function extractDocumentText(file: File): Promise<{ text: string; fileName: string }> {
  const form = new FormData()
  form.append('file', file)
  return axios
    .post(`${BASE}/extract-text/`, form)
    .then((r) => toCamelCase(r.data) as { text: string; fileName: string })
}

type DocumentUpdate = Partial<Pick<KnowledgeDocumentDetail, 'title' | 'isEnabled' | 'isPrivate' | 'folder'>>

function updateDocument(slug: string, data: DocumentUpdate): Promise<KnowledgeDocumentDetail> {
  return axios
    .patch(`${BASE}/${slug}/`, toSnakeCase(data))
    .then((r) => toCamelCase(r.data) as KnowledgeDocumentDetail)
}

function saveDraft(slug: string, draftContent: string): Promise<{ draftUpdatedAt: string }> {
  return axios
    .patch(`${BASE}/${slug}/draft/`, toSnakeCase({ draftContent }))
    .then((r) => toCamelCase(r.data) as { draftUpdatedAt: string })
}

function discardDraft(slug: string): Promise<void> {
  return axios.delete(`${BASE}/${slug}/draft/`).then(() => undefined)
}

function publishVersion(slug: string): Promise<KnowledgeDocumentVersion> {
  return axios
    .post(`${BASE}/${slug}/versions/`, {})
    .then((r) => toCamelCase(r.data) as KnowledgeDocumentVersion)
}

function revertToVersion(slug: string, versionId: string): Promise<KnowledgeDocumentVersion> {
  return axios
    .post(`${BASE}/${slug}/versions/${versionId}/revert/`, {})
    .then((r) => toCamelCase(r.data) as KnowledgeDocumentVersion)
}

export function useKnowledgeDocuments() {
  return useQuery({
    queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS],
    queryFn: listDocuments,
  })
}

export function useKnowledgeDocument(slug: string | null) {
  return useQuery({
    queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS, slug],
    queryFn: () => getDocument(slug as string),
    enabled: Boolean(slug),
  })
}

export function useKnowledgeDocumentVersions(slug: string | null) {
  return useQuery({
    queryKey: [CacheKeys.KNOWLEDGE_DOCUMENT_VERSIONS, slug],
    queryFn: () => listVersions(slug as string),
    enabled: Boolean(slug),
  })
}

export function useKnowledgeDocumentVersion(slug: string | null, versionId: string | null) {
  return useQuery({
    queryKey: [CacheKeys.KNOWLEDGE_DOCUMENT_VERSIONS, slug, versionId],
    queryFn: () => getVersion(slug as string, versionId as string),
    enabled: Boolean(slug && versionId),
  })
}

export function useCreateKnowledgeDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS] }),
  })
}

// Upload a file, extract its text on the backend, then create a KB document
// titled after the file with the extracted text as its first version.
export function useUploadKnowledgeDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, folder }: { file: File; folder?: string | null }) => {
      const { text, fileName } = await extractDocumentText(file)
      const title = fileName.replace(/\.[^/.]+$/, '').trim() || 'Untitled document'
      return createDocument({ title, content: text, folder })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS] }),
  })
}

export function useUpdateKnowledgeDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ slug, ...data }: { slug: string } & DocumentUpdate) => updateDocument(slug, data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS] })
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS, variables.slug] })
    },
  })
}

export function useSaveDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ slug, draftContent }: { slug: string; draftContent: string }) =>
      saveDraft(slug, draftContent),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS], exact: true })
      // Do NOT invalidate the detail query — would clobber in-progress edits.
    },
  })
}

export function useDiscardDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (slug: string) => discardDraft(slug),
    onSuccess: (_, slug) => {
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS] })
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS, slug] })
    },
  })
}

export function usePublishVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (slug: string) => publishVersion(slug),
    onSuccess: (_, slug) => {
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS] })
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS, slug] })
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENT_VERSIONS, slug] })
    },
  })
}

export function useRevertToVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ slug, versionId }: { slug: string; versionId: string }) =>
      revertToVersion(slug, versionId),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS] })
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS, variables.slug] })
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENT_VERSIONS, variables.slug] })
    },
  })
}
